/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import com.benasher44.uuid.uuidFrom
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.model.QuestionWithProgress
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.model.Answer
import systems.frictionless.impulse.rest.model.Assessment

// TODO [#178] This is a hack until demographics are made available as a separate endpoint.
// See systems.frictionless.impulse.assessment.model.demographics.DemographicIds
private val DEMOGRAPHIC_IDS =
    setOf(
        uuidFrom("C7CBD81A-64B5-426F-A354-0E0299C42728"),
        uuidFrom("88CADB62-3183-49BB-91B2-D7FC86CA72E5"),
        uuidFrom("7C757D92-ABC1-452F-AC80-76E2345663E4")
    )

class DemographicsViewModel(
    val authenticatedUser: AuthenticatedUser,
    val assessment: Assessment,
    private val restClient: RestClient
) {
    private val mutableState = MutableStateFlow<State>(State.Overview())

    init {
        // 🤢
        (mutableState.value as State.Overview).transitionState = {
            mutableState.tryEmit(
                State.Questions(
                    authenticatedUser,
                    assessment,
                    restClient
                ) { mutableState.tryEmit(State.Complete) }
            )
        }
    }

    val state: StateFlow<State> = mutableState

    sealed class State {
        class Overview : State() {
            internal var transitionState: () -> Unit = {}

            fun next() {
                transitionState()
            }
        }

        class Questions(
            val authenticatedUser: AuthenticatedUser,
            val assessment: Assessment,
            private val restClient: RestClient,
            private val transitionState: () -> Unit
        ) : State() {
            private val mutableStatus = MutableStateFlow<Status>(Status.Idle(null))
            val status: StateFlow<Status> = mutableStatus

            private val mutableQuestionWithProgress = MutableStateFlow<QuestionWithProgress?>(null)
            val questionWithProgress: StateFlow<QuestionWithProgress?> = mutableQuestionWithProgress

            fun fetchNextQuestion(inScope: CoroutineScope) {
                require(status.value is Status.Idle)

                mutableStatus.tryEmit(Status.Busy)

                inScope.launch {
                    when (
                        val nextQuestionResult =
                            restClient.getNextQuestion(authenticatedUser.authentication, assessment)
                    ) {
                        is RestResponse.Success -> {
                            // Need better way to do transition, but this sort of works for now
                            val nextQuestion = nextQuestionResult.result
                            if (null != nextQuestion && nextQuestion.question.id !in DEMOGRAPHIC_IDS) {
                                transitionState()
                            } else {
                                mutableQuestionWithProgress.tryEmit(nextQuestion)
                                mutableStatus.tryEmit(Status.Idle(null))
                            }
                        }
                        else -> {
                            // TODO [#116]: Convert the error code to a human-readable message.
                            // Note that we might decide this ViewModel emits an error code and that the UI does the mapping to be human-readable.
                            mutableStatus.tryEmit(Status.Idle("Error"))
                        }
                    }
                }
            }

            fun answerQuestion(
                inScope: CoroutineScope,
                answerBuilder: Answer.Builder
            ) {
                require(status.value is Status.Idle)

                mutableStatus.tryEmit(Status.Busy)
                mutableQuestionWithProgress.tryEmit(null)

                inScope.launch {
                    when (restClient.createAnswer(authenticatedUser.authentication, answerBuilder)) {
                        is RestResponse.Success -> {
                            // After answer is posted, it goes back to idle.  Clients can see that so
                            // that's how they know what to do next.
                            mutableStatus.tryEmit(Status.Idle(null))
                        }
                        else -> {
                            // TODO [#63]: Deal with errors
                            mutableStatus.tryEmit(Status.Idle("Error"))
                        }
                    }
                }
            }

            sealed class Status {
                class Idle(val errorMessage: String?) : Status()

                object Busy : Status()
            }
        }

        object Complete : State()
    }
}
